/**
 * ErrorBoundary.jsx
 * -----------------
 * React Error Boundary component that catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the entire app.
 * 
 * Features:
 * • Catches errors during rendering, in lifecycle methods, and constructors
 * • Displays user-friendly error message instead of white screen
 * • Provides retry functionality to attempt recovery
 * • Logs errors for debugging purposes
 * • Maintains app stability when components fail
 */

import React from 'react';
import { IonPage, IonContent, IonButton, IonIcon, IonText, IonCard, IonCardContent } from '@ionic/react';
import { refreshOutline, homeOutline, bugOutline } from 'ionicons/icons';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to console and potentially to an error reporting service
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Generate a unique error ID for tracking
        const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        this.setState({
            error,
            errorInfo,
            errorId
        });

        // Here you could send the error to an error reporting service like Sentry
        // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    }

    handleRetry = () => {
        // Reset the error boundary state
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        });
    };

    handleGoHome = () => {
        // Navigate to home page and reset error state
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        });
        // Force a page reload to ensure clean state
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // Fallback UI when an error occurs
            return (
                <IonPage>
                    <IonContent className="error-boundary-content">
                        <div className="error-container">
                            <IonCard className="error-card">
                                <IonCardContent>
                                    <div className="error-icon-container">
                                        <IonIcon icon={bugOutline} className="error-icon" />
                                    </div>

                                    <div className="error-content">
                                        <h2 className="error-title">Oops! Something went wrong</h2>
                                        <IonText className="error-description">
                                            <p>We encountered an unexpected error. Don't worry, this happens sometimes!</p>
                                            <p>You can try refreshing the page or go back to the home screen.</p>
                                        </IonText>

                                        {/* Show error ID for support purposes */}
                                        {this.state.errorId && (
                                            <IonText className="error-id">
                                                <small>Error ID: {this.state.errorId}</small>
                                            </IonText>
                                        )}

                                        {/* Action buttons */}
                                        <div className="error-actions">
                                            <IonButton
                                                fill="solid"
                                                color="primary"
                                                onClick={this.handleRetry}
                                                className="error-button"
                                            >
                                                <IonIcon icon={refreshOutline} slot="start" />
                                                Try Again
                                            </IonButton>

                                            <IonButton
                                                fill="outline"
                                                color="medium"
                                                onClick={this.handleGoHome}
                                                className="error-button"
                                            >
                                                <IonIcon icon={homeOutline} slot="start" />
                                                Go Home
                                            </IonButton>
                                        </div>

                                        {/* Development mode: Show error details */}
                                        {process.env.NODE_ENV === 'development' && this.state.error && (
                                            <details className="error-details">
                                                <summary>Error Details (Development Mode)</summary>
                                                <pre className="error-stack">
                                                    {this.state.error.toString()}
                                                    {this.state.errorInfo.componentStack}
                                                </pre>
                                            </details>
                                        )}
                                    </div>
                                </IonCardContent>
                            </IonCard>
                        </div>
                    </IonContent>
                </IonPage>
            );
        }

        // If no error, render children normally
        return this.props.children;
    }
}

export default ErrorBoundary;
